import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Upload, Search, Calendar, Tag } from 'lucide-react';
import { format } from 'date-fns';
import type { ScenarioSetListItem } from '@/types/supremeIntelligenceTypes';

interface LoadScenarioDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarios: ScenarioSetListItem[];
  onLoad: (id: string) => Promise<void>;
  isLoading?: boolean;
}

export const LoadScenarioDialog: React.FC<LoadScenarioDialogProps> = ({
  open,
  onOpenChange,
  scenarios,
  onLoad,
  isLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'created' | 'accessed'>('updated');
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const filteredAndSorted = useMemo(() => {
    let filtered = scenarios;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = scenarios.filter(
        (s) =>
          s.name.toLowerCase().includes(query) ||
          s.description?.toLowerCase().includes(query) ||
          s.tags?.some((t) => t.toLowerCase().includes(query))
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'created':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'accessed':
          return new Date(b.lastAccessedAt).getTime() - new Date(a.lastAccessedAt).getTime();
        case 'updated':
        default:
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
    });

    return sorted;
  }, [scenarios, searchQuery, sortBy]);

  const handleLoad = async (id: string) => {
    setLoadingId(id);
    try {
      await onLoad(id);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Load Saved Scenarios</DialogTitle>
          <DialogDescription>
            Select a scenario set to load and analyze
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search and Sort Controls */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, description, or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="updated">Recently Updated</SelectItem>
                <SelectItem value="created">Recently Created</SelectItem>
                <SelectItem value="accessed">Recently Accessed</SelectItem>
                <SelectItem value="name">Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Scenario List */}
          <ScrollArea className="h-[400px] pr-4">
            {filteredAndSorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-[300px] text-center">
                <Upload className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No saved scenarios found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery ? 'Try a different search term' : 'Save scenarios to see them here'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredAndSorted.map((scenario) => (
                  <div
                    key={scenario.id}
                    className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <h4 className="font-medium">{scenario.name}</h4>
                        {scenario.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {scenario.description}
                          </p>
                        )}
                        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(scenario.updatedAt), 'MMM d, yyyy')}
                          </span>
                          <span>•</span>
                          <span>{scenario.scenarioCount} scenarios</span>
                          {scenario.tags && scenario.tags.length > 0 && (
                            <>
                              <span>•</span>
                              <div className="flex items-center gap-1 flex-wrap">
                                <Tag className="w-3 h-3" />
                                {scenario.tags.map((tag) => (
                                  <Badge key={tag} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleLoad(scenario.id)}
                        disabled={loadingId === scenario.id || isLoading}
                      >
                        {loadingId === scenario.id ? 'Loading...' : 'Load'}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};
